/**
 * hrV1CycleService.ts
 *
 * Read-side helpers for hr_v1 cycles. Lists cycles tagged with
 * frameworkVersion='hr_v1' and produces a per-cycle health snapshot
 * (counts of submissions, appraisals, open appeals, outcomes).
 *
 * Used by the /hr-v1 index page to give HR a single view of what
 * cycles exist in the project + their current state.
 */

import {
  collection, query, where, getDocs, getCountFromServer, getDoc, doc,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import type { ReviewCycle } from '../types';

export interface HrV1CycleSnapshot {
  cycle: ReviewCycle;
  counts: {
    eprSubmissions: number;
    review360Submissions: number;
    magicLinkTokens: number;
    appraisalForms: number;
    openAppeals: number;
    outcomeRecords: number;
    calibrationAdjustments: number;
  };
  hasCalibrationSession: boolean;
}

export async function listHrV1Cycles(): Promise<ReviewCycle[]> {
  const snap = await getDocs(
    query(
      collection(db, 'reviewCycles'),
      where('frameworkVersion', '==', 'hr_v1'),
    ),
  );
  return snap.docs.map(d => ({ id: d.id, ...(d.data() as Omit<ReviewCycle, 'id'>) }));
}

export async function snapshotHrV1Cycle(cycleId: string): Promise<HrV1CycleSnapshot | null> {
  const cycleSnap = await getDoc(doc(db, 'reviewCycles', cycleId));
  if (!cycleSnap.exists()) return null;
  const cycle = { id: cycleSnap.id, ...(cycleSnap.data() as Omit<ReviewCycle, 'id'>) };

  const [eprC, r360C, tokC, appC, appealOpenC, outC, adjC, calSnap] = await Promise.all([
    countByCycleId('eprSubmissions', cycleId),
    countByCycleId('review360Submissions', cycleId),
    countByCycleId('magicLinkTokens', cycleId),
    countByCycleId('appraisalForms', cycleId),
    countByCycleIdAnd('appealRecords', cycleId, 'status', ['open', 'in_meeting']),
    countByCycleId('outcomeRecords', cycleId),
    countByCycleId('calibrationAdjustments', cycleId),
    getDoc(doc(db, 'calibrationSessions', cycleId)),
  ]);

  return {
    cycle,
    counts: {
      eprSubmissions:         eprC,
      review360Submissions:   r360C,
      magicLinkTokens:        tokC,
      appraisalForms:         appC,
      openAppeals:            appealOpenC,
      outcomeRecords:         outC,
      calibrationAdjustments: adjC,
    },
    hasCalibrationSession: calSnap.exists(),
  };
}

async function countByCycleId(col: string, cycleId: string): Promise<number> {
  const q = query(collection(db, col), where('cycleId', '==', cycleId));
  const snap = await getCountFromServer(q);
  return snap.data().count;
}

async function countByCycleIdAnd(
  col: string, cycleId: string, field: string, allowedValues: string[],
): Promise<number> {
  const q = query(
    collection(db, col),
    where('cycleId', '==', cycleId),
    where(field, 'in', allowedValues),
  );
  const snap = await getCountFromServer(q);
  return snap.data().count;
}

export async function snapshotAllHrV1Cycles(): Promise<HrV1CycleSnapshot[]> {
  const cycles = await listHrV1Cycles();
  // Per-cycle snapshots in parallel; ignore individual failures so one
  // permission denial doesn't take down the whole list.
  const results = await Promise.allSettled(cycles.map(c => snapshotHrV1Cycle(c.id)));
  const out: HrV1CycleSnapshot[] = [];
  for (const r of results) {
    if (r.status === 'fulfilled' && r.value) out.push(r.value);
  }
  return out;
}
